import { message } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';

import { trimMappedEventRules } from '@components/mapping-panel/utils/trimUtils';
import { validateMappedEventData } from '@components/mapping-panel/utils/validateUtils';
import { IMappedEvent } from '@src/pages/metric-templates/types';
import useLatest from '@hooks/useLatest';
import MappedEventBuilder from '@src/emp-3.0/components/MappedEventBuilder';
import { isEqual } from 'lodash';
import { DEFAULT_REQUIRED_ERROR_MESSAGE } from '@src/emp-3.0/constants/common';

interface IProps {
  event: IMappedEvent;
  isLoading?: boolean;
  onChange?: (event: IMappedEvent | number) => void;
  onSave?: (event?: IMappedEvent) => void;
  onCancel?: (event?: IMappedEvent) => void;
}

const EventBuilder: React.FC<IProps> = ({ event, isLoading, onChange, onSave, onCancel }) => {
  const [validation, setValidation] = useState<{
    failed: boolean;
    fields: string[];
    message?: string;
    errorIndex?: Record<number, number[]>;
  }>({ failed: false, fields: [] });
  const latestValidationRef = useLatest(validation);

  const trimMappedEvent = useMemo(() => {
    if (!event) return undefined;
    return {
      ...event,
      rules: trimMappedEventRules(event?.rules),
    };
  }, [event]);

  const handleCancelClick = (event) => {
    onCancel?.(event);
  };

  const onEventChange = (event) => {
    onChange?.(event);
  };
  const onValidate = () => {
    const eventNew = {
      ...event,
      rules: trimMappedEventRules(event?.rules),
    };

    const res = validateMappedEventData(eventNew, true);
    if (!res.status) {
      if (!res.fields.length) {
        message.warning(`${res.key ? res.key.join(' OR ') : ''} ${res.result}`);
      } else {
        const message =
          res.result === 'is null' ? DEFAULT_REQUIRED_ERROR_MESSAGE : `${res.key} ${res.result}`;
        setValidation({
          failed: true,
          fields: res.fields,
          message: message,
          errorIndex: res.errorIndex,
        });
      }
    }
    return res.status;
  };

  const onSaveClick = (event) => {
    onSave?.(event);
  };

  useEffect(() => {
    if (trimMappedEvent) {
      const res = validateMappedEventData(trimMappedEvent, true);
      if (isEqual(latestValidationRef.current.fields, res.fields)) {
        setValidation({ failed: false, fields: [] });
      }
    }
  }, [trimMappedEvent]);

  const eventBuilder = (
    <MappedEventBuilder
      validation={validation}
      setValidation={setValidation}
      loading={isLoading}
      data={event}
      onCancel={handleCancelClick}
      onMap={(event) => {
        onSaveClick(event);
      }}
      onSwitch={(eventId) => {
        onChange?.(eventId);
      }}
      onChange={onEventChange}
      onValidate={onValidate}
    />
  );
  return eventBuilder;
};

export default React.memo(EventBuilder, (prevProps, nextProps) => {
  return isEqual(prevProps, nextProps);
});
